import React from 'react'

import { Badge, Button, Space, Table, Typography } from 'antd'

import { BillingTransaction } from '@pollination-solutions/pollination-sdk'
import { formatCurrency } from './billingUtils'


type Props = {
  transactions: BillingTransaction[]
  transactionsLoading: boolean
  transactionsTabInitialized: boolean
  onRefreshTransactions: () => void
}

export const SubscriptionTransactions: React.FC<Props> = ({
  transactions,
  transactionsLoading,
  transactionsTabInitialized,
  onRefreshTransactions
}) => {

  return (
    <div style={{ minHeight: '400px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Transaction History
          </Typography.Title>
          <Button onClick={onRefreshTransactions} loading={transactionsLoading}>
            Refresh
          </Button>
        </div>

        <div style={{
          width: '100%',
          minHeight: '300px',
          position: 'relative'
        }}>
          {!transactionsTabInitialized || transactionsLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px'
            }}>
              <Space direction="vertical" align="center">
                <div>{transactionsLoading ? 'Loading transactions...' : 'Initializing...'}</div>
              </Space>
            </div>
          ) : (
            <Table<BillingTransaction>
              dataSource={transactions || []}
              loading={false}
              rowKey={(record) => record.id || `transaction-${Date.now()}-${Math.random()}`}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`
              }}
              columns={[
                {
                  title: 'Created',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (date: string) => {
                    try {
                      return date ? new Date(date).toLocaleString() : 'N/A'
                    } catch (error) {
                      return 'Invalid Date'
                    }
                  },
                  sorter: (a, b) => {
                    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
                    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
                    return dateA - dateB
                  },
                  defaultSortOrder: 'descend',
                },
                {
                  title: 'Amount',
                  key: 'amount',
                  render: (_, record: BillingTransaction) => {
                    try {
                      if (!record.currency_code) return 'N/A'
                      // Calculate total from transaction items
                      return (
                        <Typography.Text strong>
                          {formatCurrency(record.details?.totals?.grand_total || '0', record.currency_code)}
                        </Typography.Text>
                      )
                    } catch (error) {
                      return 'N/A'
                    }
                  },
                  sorter: (a, b) => {
                    try {
                      const amountA = a.items?.reduce((total, item) => {
                        const itemAmount = parseFloat(item.price?.unit_price?.amount || '0')
                        return total + (itemAmount * (item.quantity || 1))
                      }, 0) || 0
                      const amountB = b.items?.reduce((total, item) => {
                        const itemAmount = parseFloat(item.price?.unit_price?.amount || '0')
                        return total + (itemAmount * (item.quantity || 1))
                      }, 0) || 0
                      return amountA - amountB
                    } catch (error) {
                      return 0
                    }
                  },
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status: string) => {
                    if (!status) return 'N/A'

                    const statusColors = {
                      'completed': 'green',
                      'paid': 'green',
                      'pending': 'orange',
                      'failed': 'red',
                      'cancelled': 'red',
                      'draft': 'blue',
                      'ready': 'blue'
                    }
                    const color = statusColors[status.toLowerCase() as keyof typeof statusColors] || 'default'

                    return (
                      <Badge
                        color={color}
                        text={status.charAt(0).toUpperCase() + status.slice(1)}
                      />
                    )
                  },
                  filters: [
                    { text: 'Completed', value: 'completed' },
                    { text: 'Paid', value: 'paid' },
                    { text: 'Pending', value: 'pending' },
                    { text: 'Failed', value: 'failed' },
                    { text: 'Cancelled', value: 'cancelled' },
                    { text: 'Draft', value: 'draft' },
                    { text: 'Ready', value: 'ready' }
                  ],
                  onFilter: (value, record) => Boolean(record.status && record.status.toLowerCase() === value),
                },
                {
                  title: 'Invoice',
                  dataIndex: 'invoice_number',
                  key: 'invoice_number',
                  render: (invoiceNumber: string) => {
                    if (!invoiceNumber) return <Typography.Text type="secondary">-</Typography.Text>
                    return (
                      <Typography.Text code style={{ fontSize: '11px' }}>
                        {invoiceNumber}
                      </Typography.Text>
                    )
                  },
                },
                {
                  title: 'Collection Mode',
                  dataIndex: 'collection_mode',
                  key: 'collection_mode',
                  render: (mode: string) => {
                    if (!mode) return 'N/A'
                    return (
                      <Typography.Text style={{ fontSize: '12px', textTransform: 'capitalize' }}>
                        {mode == 'automatic' ? 'Card' : 'Bank Transfer'}
                      </Typography.Text>
                    )
                  },
                },
                {
                  title: 'Updated',
                  dataIndex: 'updated_at',
                  key: 'updated_at',
                  render: (date: string) => {
                    try {
                      return (
                        <Typography.Text style={{ fontSize: '12px' }}>
                          {date ? new Date(date).toLocaleString() : 'N/A'}
                        </Typography.Text>
                      )
                    } catch (error) {
                      return 'Invalid Date'
                    }
                  },
                }
              ]}
              scroll={{ x: 1200, y: 400 }}
              size="small"
              bordered={false}
            />
          )}
        </div>

        {transactions.length === 0 && !transactionsLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            <Typography.Text type="secondary">
              No transactions found for this subscription
            </Typography.Text>
          </div>
        )}
      </Space>
    </div>
  )
}